import * as React from 'react';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { fmtAcceptedAt } from '../helpers/clubProgrammeBookingsBoard';

export type ConfirmConflictOption = {
  messageId: string;
  clubId: string;
  clubName: string;
  isAlreadyConfirmed: boolean;
  priority?: number;
  acceptedAt?: string | null;
  dayKey?: string;
};

export type ConfirmConflictStudent = {
  userId: string;
  permissionId: string;
  name?: string;
};

export type ConfirmConflictItem = {
  dayKey?: string;
  permissionId: string;
  student: ConfirmConflictStudent;
  options: ConfirmConflictOption[];
};

type ResolutionMode = 'club' | 'day' | 'programme';

type ClubOrDayResolution = {
  permissionId: string;
  keepMessageId: string;
};

type ProgrammeResolution = {
  dayKey: string;
  permissionId: string;
  keepMessageId: string;
};

type Props = {
  isOpen: boolean;
  title?: string;
  description?: string;
  conflicts: ConfirmConflictItem[];
  mode: ResolutionMode;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (resolutions: ClubOrDayResolution[] | ProgrammeResolution[]) => void;
};

function getConflictKey(conflict: ConfirmConflictItem, mode: ResolutionMode): string {
  if (mode === 'programme') {
    return `${String(conflict.dayKey || '')}:${String(conflict.permissionId || '')}`;
  }

  return String(conflict.permissionId || '');
}

function getOptionLabel(option: ConfirmConflictOption): string {
  const parts: string[] = [];

  parts.push(String(option.clubName || 'Club'));

  if (typeof option.priority === 'number') {
    parts.push(`P${option.priority}`);
  }

  if (option.acceptedAt) {
    parts.push(`Accepted ${fmtAcceptedAt(option.acceptedAt)}`);
  }

  return parts.join(' • ');
}

export const ClubProgrammeConfirmConflictsModal: React.FC<Props> = ({
  isOpen,
  title,
  description,
  conflicts,
  mode,
  isSubmitting = false,
  onClose,
  onSubmit
}) => {
  const [selectedByKey, setSelectedByKey] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedByKey({});
      return;
    }

    const initialState: Record<string, string> = {};

    (Array.isArray(conflicts) ? conflicts : []).forEach(conflict => {
      const key = getConflictKey(conflict, mode);
      const confirmedOption = (conflict.options || []).find(opt => !!opt.isAlreadyConfirmed);

      if (confirmedOption?.messageId) {
        initialState[key] = String(confirmedOption.messageId);
      }
    });

    setSelectedByKey(initialState);
  }, [isOpen, conflicts, mode]);

  if (!isOpen) return null;

  const safeConflicts = Array.isArray(conflicts) ? conflicts : [];

  const allResolved =
    safeConflicts.length > 0 &&
    safeConflicts.every(conflict => {
      const key = getConflictKey(conflict, mode);
      const selectedMessageId = String(selectedByKey[key] || '');

      if (!selectedMessageId) return false;

      return (conflict.options || []).some(option => String(option.messageId || '') === selectedMessageId);
    });

  const handleChange = (conflict: ConfirmConflictItem, messageId: string) => {
    const key = getConflictKey(conflict, mode);

    setSelectedByKey(prev => ({
      ...prev,
      [key]: String(messageId || '')
    }));
  };

  const handleSubmit = () => {
    if (!allResolved || isSubmitting) return;

    if (mode === 'programme') {
      const payload: ProgrammeResolution[] = safeConflicts.map(conflict => {
        const key = getConflictKey(conflict, mode);

        return {
          dayKey: String(conflict.dayKey || ''),
          permissionId: String(conflict.permissionId || ''),
          keepMessageId: String(selectedByKey[key] || '')
        };
      });

      onSubmit(payload);
      return;
    }

    const payload: ClubOrDayResolution[] = safeConflicts.map(conflict => {
      const key = getConflictKey(conflict, mode);

      return {
        permissionId: String(conflict.permissionId || ''),
        keepMessageId: String(selectedByKey[key] || '')
      };
    });

    onSubmit(payload);
  };

  return (
    <SimpleModal
      isOpen={true}
      title={title || 'Resolve allocation conflicts'}
      showFooter={false}
      onCloseClick={isSubmitting ? () => null : onClose}
      customClass="pbBookingModal pbBookingConfirmConflictsModal"
      dialogStyle={{ maxWidth: 820 }}
    >
      <div className="p-2">
        <div className="alert alert-warning">
          {description ||
            'Some students are currently linked to multiple club allocations for the same day. Choose which club should be kept for each student before continuing.'}
        </div>

        {!safeConflicts.length ? (
          <div className="alert alert-light border mb-0">No conflicts to resolve.</div>
        ) : (
          <div className="d-flex flex-column" style={{ gap: 12 }}>
            {safeConflicts.map((conflict, index) => {
              const conflictKey = getConflictKey(conflict, mode);
              const selectedMessageId = String(selectedByKey[conflictKey] || '');

              return (
                <div key={`${conflictKey}:${index}`} className="border rounded p-3" style={{ background: '#fff' }}>
                  <div className="d-flex flex-wrap align-items-start justify-content-between mb-2">
                    <div>
                      <div className="font-weight-bold">{String(conflict.student?.name || 'Student')}</div>

                      <div className="small text-muted">Permission ID: {String(conflict.permissionId || '')}</div>

                      {mode === 'programme' && conflict.dayKey ? (
                        <div className="small text-muted">Day: {String(conflict.dayKey)}</div>
                      ) : null}
                    </div>

                    <span className="badge badge-danger">{(conflict.options || []).length} options</span>
                  </div>

                  <div className="d-flex flex-column" style={{ gap: 10 }}>
                    {(conflict.options || []).map(option => {
                      const optionMessageId = String(option.messageId || '');
                      const checked = selectedMessageId === optionMessageId;

                      return (
                        <label
                          key={optionMessageId}
                          className="border rounded p-2 mb-0"
                          style={{
                            cursor: isSubmitting ? 'default' : 'pointer',
                            background: checked ? '#f4fbff' : '#fff',
                            borderColor: checked ? '#17a2b8' : undefined
                          }}
                        >
                          <div className="d-flex align-items-start">
                            <input
                              type="radio"
                              name={`confirm-conflict-${conflictKey}`}
                              value={optionMessageId}
                              checked={checked}
                              disabled={isSubmitting}
                              onChange={() => handleChange(conflict, optionMessageId)}
                              style={{ marginTop: 4, marginRight: 10 }}
                            />

                            <div className="flex-grow-1">
                              <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                                <span className="font-weight-bold">{String(option.clubName || 'Club')}</span>

                                {option.isAlreadyConfirmed ? (
                                  <span className="badge badge-success">Already confirmed</span>
                                ) : (
                                  <span className="badge badge-info">New choice</span>
                                )}

                                {mode === 'programme' && option.dayKey ? (
                                  <span className="badge badge-light border">{String(option.dayKey)}</span>
                                ) : null}
                              </div>

                              <div className="small text-muted mt-1">{getOptionLabel(option)}</div>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="d-flex justify-content-end mt-3" style={{ gap: 8 }}>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!allResolved || isSubmitting || !safeConflicts.length}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </SimpleModal>
  );
};

ClubProgrammeConfirmConflictsModal.displayName = 'ClubProgrammeConfirmConflictsModal';
